import { BaseRepository } from './BaseRepository';

/**
 * SuccessWare Master Field type definition
 */
export interface SuccessWareMasterField {
  id: string;
  field_name: string;
  field_path: string;
  entity_type: string;
  database_table: string | null;
  database_column: string | null;
  edge_function: string | null;
  is_imported: boolean;
  json_type: string | null;
  push_pull: string | null;
  description: string | null;
  notes: string | null;
  examples: string | null;
  created_at: string;
  updated_at: string;
}

export class SuccessWareMasterFieldsRepository extends BaseRepository<SuccessWareMasterField> {
  constructor() {
    super({ tableName: 'successware_master_fields', primaryKey: 'id' });
  }

  /**
   * Get all fields with optional entity type filter
   */
  async getAllFields(entityType?: string) {
    let query = this.client
      .from('successware_master_fields')
      .select('*')
      .order('entity_type', { ascending: true })
      .order('field_name', { ascending: true });

    if (entityType) {
      query = query.eq('entity_type', entityType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as SuccessWareMasterField[];
  }

  /**
   * Get fields by entity type
   */
  async getFieldsByEntity(entityType: string) {
    const { data, error } = await this.client
      .from('successware_master_fields')
      .select('*')
      .eq('entity_type', entityType)
      .order('field_name', { ascending: true });

    if (error) throw error;
    return data as SuccessWareMasterField[];
  }

  /**
   * Get field by entity and field name
   */
  async getField(entityType: string, fieldName: string) {
    const { data, error } = await this.client
      .from('successware_master_fields')
      .select('*')
      .eq('entity_type', entityType)
      .eq('field_name', fieldName)
      .single();

    if (error) throw error;
    return data as SuccessWareMasterField;
  }

  /**
   * Update field description or notes (user-editable fields)
   */
  async updateField(
    id: string,
    updates: Pick<SuccessWareMasterField, 'description' | 'notes'>
  ) {
    const { data, error } = await this.client
      .from('successware_master_fields')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as SuccessWareMasterField;
  }

  /**
   * Search fields by text (searches field_name, description, and notes)
   */
  async searchFields(searchText: string, entityType?: string) {
    let query = this.client
      .from('successware_master_fields')
      .select('*')
      .or(`field_name.ilike.%${searchText}%,description.ilike.%${searchText}%,notes.ilike.%${searchText}%`)
      .order('entity_type', { ascending: true })
      .order('field_name', { ascending: true });

    if (entityType) {
      query = query.eq('entity_type', entityType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as SuccessWareMasterField[];
  }

  /**
   * Get unique entity types
   */
  async getEntityTypes() {
    const { data, error } = await this.client
      .from('successware_master_fields')
      .select('entity_type')
      .order('entity_type', { ascending: true });

    if (error) throw error;

    // Get unique entity types
    const uniqueTypes = [...new Set(data.map((row: any) => row.entity_type))];
    return uniqueTypes;
  }

  /**
   * Get field statistics by entity type
   */
  async getFieldStats() {
    const { data, error} = await this.client
      .from('successware_master_fields')
      .select('entity_type, is_imported');

    if (error) throw error;

    // Group and count by entity type
    const stats = data.reduce((acc: Record<string, { entity_type: string; total_fields: number; imported_fields: number }>, field: any) => {
      if (!acc[field.entity_type]) {
        acc[field.entity_type] = {
          entity_type: field.entity_type,
          total_fields: 0,
          imported_fields: 0,
        };
      }
      acc[field.entity_type].total_fields++;
      if (field.is_imported) {
        acc[field.entity_type].imported_fields++;
      }
      return acc;
    }, {} as Record<string, { entity_type: string; total_fields: number; imported_fields: number }>);

    return Object.values(stats);
  }

  /**
   * Get fields that are actively imported
   */
  async getImportedFields(entityType?: string) {
    let query = this.client
      .from('successware_master_fields')
      .select('*')
      .eq('is_imported', true)
      .order('entity_type', { ascending: true })
      .order('field_name', { ascending: true });

    if (entityType) {
      query = query.eq('entity_type', entityType);
    }

    const { data, error } = await query;

    if (error) throw error;
    return data as SuccessWareMasterField[];
  }
}
